import { createContext, useContext, useState, useEffect } from 'react';
import api from '../lib/api';

const ADMIN_AURA_COLORS = {
  golden: ['#D4AF37', '#F5D76E', '#C5A028'],
  silver: ['#C0C0C0', '#E8E8E8', '#A0A0A0'],
  ruby: ['#E31B23', '#FF4B4B', '#B91C1C'],
  emerald: ['#50C878', '#2ECC71', '#27AE60'],
  sapphire: ['#0F52BA', '#4169E1', '#1E40AF'],
  amethyst: ['#9966CC', '#B19CD9', '#7B68EE'],
  obsidian: ['#1A1A1A', '#2D2D2D', '#404040'],
  sunset: ['#FF6B35', '#F7931E', '#FFB347']
};

const AdminAuraContext = createContext(null);

export const AdminAuraProvider = ({ children }) => {
  const [auraPreset, setAuraPreset] = useState(null);
  const [currentColors, setCurrentColors] = useState(['#D4AF37', '#F5D76E', '#C5A028']);

  // Load aura from user profile on mount
  useEffect(() => {
    const loadUserAura = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        // Use a direct fetch to avoid the interceptor causing redirects
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${API_URL}/api/auth/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data?.aura_preset) {
            setAuraPreset(data.aura_preset);
            setCurrentColors(ADMIN_AURA_COLORS[data.aura_preset] || ADMIN_AURA_COLORS.golden);
          }
        }
        // If not ok, silently fail - don't redirect or error
      } catch (error) {
        // Silently fail - aura is not critical
        console.log('Could not load user aura');
      }
    };
    
    loadUserAura();
  }, []);

  // Update colors when preset changes
  useEffect(() => {
    if (auraPreset && ADMIN_AURA_COLORS[auraPreset]) {
      setCurrentColors(ADMIN_AURA_COLORS[auraPreset]);
    }
  }, [auraPreset]);

  const updateAura = (preset) => {
    setAuraPreset(preset);
    if (ADMIN_AURA_COLORS[preset]) {
      setCurrentColors(ADMIN_AURA_COLORS[preset]);
    }
  };

  return (
    <AdminAuraContext.Provider value={{
      auraPreset,
      currentColors,
      updateAura,
      AURA_COLORS: ADMIN_AURA_COLORS
    }}>
      {children}
    </AdminAuraContext.Provider>
  );
};

export const useAdminAura = () => {
  const context = useContext(AdminAuraContext);
  if (!context) {
    // Return default values if used outside provider
    return {
      auraPreset: null,
      currentColors: ['#D4AF37', '#F5D76E', '#C5A028'],
      updateAura: () => {},
      AURA_COLORS: ADMIN_AURA_COLORS
    };
  }
  return context;
};

export default AdminAuraContext;
